import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  // Lightweight calls get one automatic retry when the gateway answers 502/503/504
  // (typical during a production rollout). Heavy/multipart calls opt out below.
  retryUnavailable: true,
});

const UNAVAILABLE = new Set([502, 503, 504]);
const RETRY_DELAY_MS = 1500;
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Global response interceptor - handle 401 errors + one retry on gateway unavailability
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config || {};
    const status = error.response?.status;

    if (status && UNAVAILABLE.has(status) && config.retryUnavailable && !config.__retried) {
      config.__retried = true;
      await wait(RETRY_DELAY_MS);
      return api.request(config);
    }

    if (status === 401) {
      // Token expired or invalid - clean up and redirect
      localStorage.removeItem('token');
      if (window.location.pathname !== '/auth') {
        window.location.href = '/auth';
      }
    }
    return Promise.reject(error);
  }
);

// Helper to get auth header
const getAuth = () => {
  const token = localStorage.getItem('token');
  return token ? `Bearer ${token}` : '';
};

// Auth
export const authAPI = {
  register: (data) => api.post('/api/auth/register', data),
  login: (data) => api.post('/api/auth/login', data),
  verify: () => api.post('/api/auth/verify', { authorization: getAuth() }),
  guest: (language = 'fr') => api.post('/api/auth/guest', { language }),
};

// Profile
export const profileAPI = {
  consent: (accepted = true) => api.post('/api/profile/consent', { authorization: getAuth(), accepted }),
  update: (profile) => api.post('/api/profile/update', { authorization: getAuth(), profile }),
  updateLanguage: (language) => api.post('/api/profile/language', { authorization: getAuth(), language }),
};

// Chat
export const chatAPI = {
  send: (message, language = 'fr', sessionId = null) =>
    api.post('/api/chat/send', {
      authorization: getAuth(),
      message,
      language,
      session_id: sessionId
    }, { retryUnavailable: false }),
};

// Photo Analysis
export const analyzeAPI = {
  photo: (file, language = 'fr', sessionId = null) => {
    const formData = new FormData();
    formData.append('photo', file);
    formData.append('authorization', getAuth());
    formData.append('language', language);
    if (sessionId) formData.append('session_id', sessionId);
    return api.post('/api/analyze/photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
      retryUnavailable: false,
    });
  },
  simulate: (file, language = 'fr') => {
    const formData = new FormData();
    formData.append('photo', file);
    formData.append('authorization', getAuth());
    formData.append('language', language);
    // Starts an async job; poll simulateStatus(job_id) until status is 'done' or 'failed'.
    return api.post('/api/analyze/simulate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 45000,
      retryUnavailable: false,
    });
  },
  simulateStatus: (jobId) =>
    api.post('/api/analyze/simulate/status', { authorization: getAuth(), job_id: jobId }, { timeout: 20000 }),
  // Starts a background job (202). Poll jobsAPI.status(job_id); result = { session_id }
  full: (language = 'fr') =>
    api.post('/api/analyze/full', { authorization: getAuth(), language }, { retryUnavailable: false }),
};

// Background jobs (simulation, full analysis, routine)
export const jobsAPI = {
  status: (jobId) => api.post('/api/jobs/status', { authorization: getAuth(), job_id: jobId }, { timeout: 20000 }),
};

// Saved simulations (history)
export const simulationAPI = {
  list: () => api.post('/api/simulations/list', { authorization: getAuth() }),
  get: (sessionId) => api.post('/api/simulations/get', { authorization: getAuth(), session_id: sessionId }, { timeout: 30000 }),
};

// Admin: support inbox
export const adminAPI = {
  supportList: (status = 'new', search = '') =>
    api.post('/api/admin/support/list', { authorization: getAuth(), status, search: search || null }),
  supportUpdate: (id, status, note = null) =>
    api.post('/api/admin/support/update', { authorization: getAuth(), id, status, note }),
  // Sends a real email (Brevo) to the requester and stores it in the ticket thread
  supportReply: (id, body, markHandled = true) =>
    api.post('/api/admin/support/reply', { authorization: getAuth(), id, body, mark_handled: markHandled }, { timeout: 30000, retryUnavailable: false }),
};

// Sessions
export const sessionAPI = {
  list: () => api.post('/api/sessions/list', { authorization: getAuth() }),
  messages: (sessionId) => api.post('/api/sessions/messages', { authorization: getAuth(), session_id: sessionId }),
  delete: (sessionId) => api.post('/api/sessions/delete', { authorization: getAuth(), session_id: sessionId }),
};

// Progress
export const progressAPI = {
  add: (metricType, value, notes = '') =>
    api.post('/api/progress/add', { authorization: getAuth(), metric_type: metricType, value, notes }, { retryUnavailable: false }),
  list: (metricType = null) =>
    api.post('/api/progress/list', { authorization: getAuth(), metric_type: metricType }),
};

// Routine
export const routineAPI = {
  // Starts a background job (202). Poll jobsAPI.status(job_id); result = { routine }
  generate: (language = 'fr') =>
    api.post('/api/routine/generate', { authorization: getAuth(), language }, { retryUnavailable: false }),
  get: () => api.post('/api/routine/get', { authorization: getAuth() }),
  downloadPdf: (language = 'fr') =>
    api.post('/api/routine/pdf', { authorization: getAuth(), language }, { responseType: 'blob' }),
};

// Reminders
export const reminderAPI = {
  save: (reminders, timezone = null) =>
    api.post('/api/reminders/save', { authorization: getAuth(), reminders, timezone }),
  get: () => api.post('/api/reminders/get', { authorization: getAuth() }),
  logs: () => api.post('/api/reminders/logs', { authorization: getAuth() }),
  sendTest: (category = 'checkin') =>
    api.post('/api/reminders/test', { authorization: getAuth(), category }, { timeout: 60000, retryUnavailable: false }),
};

// Support / Contact (stored in-app only, no email is sent)
export const supportAPI = {
  contact: ({ subject, message, contactEmail, language = 'fr' }) =>
    api.post('/api/support/contact', {
      authorization: getAuth(),
      subject,
      message,
      contact_email: contactEmail || null,
      language,
    }, { retryUnavailable: false }),
  mine: () => api.post('/api/support/mine', { authorization: getAuth() }),
};

// Dashboard
export const dashboardAPI = {
  stats: () => api.post('/api/dashboard/stats', { authorization: getAuth() }),
};

export default api;
